import { ShieldCheck, ShieldMinus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/client/components/confirm-dialog";
import { PageHeader } from "@/client/components/page-header";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import { Input } from "@/client/components/ui/input";
import { Skeleton } from "@/client/components/ui/skeleton";
import { type AdminUser, usersAdminApi } from "@/client/features/admin/users-admin-api";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { ApiError } from "@/client/lib/api";

function displayName(u: AdminUser): string {
  return u.name?.trim() || u.email;
}

/**
 * Staff role actions for one staff row. Promotion is a direct action; demotion
 * goes through the shared {@link ConfirmDialog} since it can remove someone's
 * ability to create tenants. The server independently enforces "no self-change"
 * and "keep one admin", so this UI only needs to make the intent clear. Labels
 * line up under the right-aligned "Actions" header via a shared `pr-3` (matching
 * the ghost button's own right padding) - no negative margins, so nothing spills
 * past the table edge and forces a horizontal scrollbar.
 */
function StaffRoleAction({
  user,
  isSelf,
  onChanged,
}: {
  user: AdminUser;
  isSelf: boolean;
  onChanged: (u: AdminUser) => void;
}) {
  const [busy, setBusy] = useState(false);

  if (isSelf) {
    return <span className="text-muted-foreground pr-3 text-xs">You</span>;
  }

  const apply = async (staffRole: "admin" | "staff") => {
    const updated = await usersAdminApi.setStaffRole(user.id, staffRole);
    onChanged(updated);
    toast.success(
      staffRole === "admin"
        ? `${displayName(user)} is now an admin.`
        : `${displayName(user)} is now a regular staff member.`,
    );
  };

  if (user.staffRole === "admin") {
    return (
      <ConfirmDialog
        trigger={
          <Button variant="ghost" size="sm">
            <ShieldMinus className="size-4" />
            Demote to staff
          </Button>
        }
        title={`Demote ${displayName(user)}?`}
        description="They'll lose admin actions, including creating tenants and managing staff roles. They keep their staff access otherwise. This takes effect on their next request."
        confirmLabel="Demote to staff"
        pendingLabel="Demoting…"
        errorMessage="Couldn't update the role."
        onConfirm={() => apply("staff")}
      />
    );
  }

  const promote = async () => {
    setBusy(true);
    try {
      await apply("admin");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update the role.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button variant="ghost" size="sm" onClick={promote} disabled={busy}>
      <ShieldCheck className="size-4" />
      {busy ? "Promoting…" : "Make admin"}
    </Button>
  );
}

export function StaffUsersPage() {
  const self = useAuthStore((s) => s.user);
  const canManage = self?.staffRole === "admin";
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");

  const refresh = async () => {
    try {
      const res = await usersAdminApi.list();
      // This view is staff only; customer accounts are managed on their tenant's
      // page, so keep them out of here.
      setUsers(res.users.filter((u) => u.kind === "staff"));
      setError(false);
    } catch (err) {
      setError(true);
      toast.error(err instanceof ApiError ? err.message : "Failed to load staff.");
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const onChanged = (updated: AdminUser) => {
    setUsers((prev) => (prev ? prev.map((u) => (u.id === updated.id ? updated : u)) : prev));
  };

  const filtered = useMemo(() => {
    if (!users) return [];
    const q = query.trim().toLowerCase();
    return users
      .filter(
        (u) =>
          q === "" || u.email.toLowerCase().includes(q) || (u.name ?? "").toLowerCase().includes(q),
      )
      .sort((a, b) => displayName(a).localeCompare(displayName(b)));
  }, [users, query]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Staff" description="Mantle staff who can sign in to the portal." />

      <Card>
        <CardHeader>
          <CardTitle>Staff members</CardTitle>
          <CardDescription>
            {canManage
              ? "Promote a staff member to admin to let them create tenants and manage roles."
              : "Mantle staff accounts and their roles."}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Input
            placeholder="Search name or email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-xs"
          />

          {error && users === null ? (
            <p className="text-muted-foreground text-sm">
              Couldn't load staff. Refresh to try again.
            </p>
          ) : users === null ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground text-sm">No staff match.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b text-left">
                    <th className="py-2 pr-4 font-medium">Name</th>
                    <th className="py-2 pr-4 font-medium">Email</th>
                    <th className="py-2 pr-4 font-medium">Role</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    {canManage ? (
                      <th className="py-2 pr-3 text-right font-medium">Actions</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => (
                    <tr key={u.id} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium">{u.name?.trim() || "—"}</td>
                      <td className="py-2 pr-4 font-mono text-xs">{u.email}</td>
                      <td className="py-2 pr-4">
                        {u.staffRole === "admin" ? (
                          <Badge variant="default">Admin</Badge>
                        ) : (
                          <Badge variant="secondary">Staff</Badge>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {u.status === "active" ? (
                          <span className="text-muted-foreground">Active</span>
                        ) : (
                          <Badge variant="destructive">Disabled</Badge>
                        )}
                      </td>
                      {canManage ? (
                        <td className="py-2 text-right">
                          <StaffRoleAction
                            user={u}
                            isSelf={u.id === self?.id}
                            onChanged={onChanged}
                          />
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
